import { THIRD_PARTY_DISCLOSURE_DRAFT } from '@orn/global/billing/mandateText.js';
import Button from '@orn/shared-web/components/Button/Button';
import Checkbox from '@orn/shared-web/components/Checkbox';
import * as React from 'react';

import { interpolateMandate } from './interpolateMandate';

export interface IMandateAuthorizationBaseProps {
  /** The account being debited, restated here so the terms name it. */
  institution: string;
  mask: string;
  /** Server-formatted, rendered verbatim — the same strings the evidence row stores. */
  amountDisplay: string;
  firstChargeDisplay: string;
  /**
   * Whether the owner-name check came back as anything other than a match.
   * Shows the disclosure and makes the acknowledgment required.
   */
  requiresThirdPartyAck: boolean;
  thirdPartyAck: boolean;
  onThirdPartyAckChange: (acknowledged: boolean) => void;
  onSubmit: () => void;
  /** Returns to the previous checkout screen with the selection kept. */
  onBack: () => void;
  isSubmitting: boolean;
  /**
   * The refusal's headline, naming the state the account is in. Says what is
   * true, never why — the provider's codes do not establish a cause, so a
   * title asserting one would be a guess rendered in bold.
   */
  errorTitle?: string;
  /** Typed refusal text, already mapped to something a subscriber can act on. */
  errorMessage?: string;
  /**
   * The control that answers the refusal, for a refusal the subscriber can
   * clear without leaving the screen. Rendered under the message, so a refusal
   * that only needs reading passes nothing.
   */
  errorAction?: React.ReactNode;
  /** What submitting commits the subscriber to — each host names its own action. */
  submitLabel: string;
  /**
   * The mandate template this host displays, from the registry matching the
   * version it submits — the pair is what the evidence row proves, so they
   * must come from the same registry entry.
   */
  mandateTemplate: string;
  /** The immediate-cost sentence, e.g. "$0 today. $99.00/year starting …" — the moment differs per host. */
  chargeSummary: string;
}

/**
 * The ACH debit authorization screen, shared by checkout and the
 * payment-account switch.
 *
 * States the amount, cadence, the account, revocation rights, and the
 * statement descriptor, because a WEB-debit authorization is required to. The
 * text is rendered from the shared template so the version identifier stored
 * on the mandate row names copy that actually shipped.
 */
const MandateAuthorizationBase = ({
  institution,
  mask,
  amountDisplay,
  firstChargeDisplay,
  requiresThirdPartyAck,
  thirdPartyAck,
  onThirdPartyAckChange,
  onSubmit,
  onBack,
  isSubmitting,
  errorTitle,
  errorMessage,
  errorAction,
  submitLabel,
  mandateTemplate,
  chargeSummary,
}: IMandateAuthorizationBaseProps) => {
  const values = { institution, mask, amount: amountDisplay, firstChargeDate: firstChargeDisplay };
  const blockedOnAck = requiresThirdPartyAck && !thirdPartyAck;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="border-secondary flex w-full flex-col gap-1 rounded-xl border p-4">
        <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">Paying from</p>
        <p className="text-primary text-md font-medium">
          {institution} ••{mask}
        </p>
        <p className="text-tertiary text-sm">{chargeSummary}</p>
      </div>

      {errorMessage !== undefined && (
        // Sits with the account rather than above the submit: it is a state of
        // the payment method, and reading it there is what tells the
        // subscriber the card above is the thing needing attention.
        <div className="bg-error-secondary text-error-primary flex flex-col items-start gap-2 rounded-lg px-3 py-3 text-sm">
          {errorTitle !== undefined && <p className="font-semibold">{errorTitle}</p>}
          <p>{errorMessage}</p>
          {errorAction !== undefined && <div className="pt-1">{errorAction}</div>}
        </div>
      )}

      {requiresThirdPartyAck && (
        <Checkbox
          isSelected={thirdPartyAck}
          onChange={onThirdPartyAckChange}
          label={interpolateMandate(THIRD_PARTY_DISCLOSURE_DRAFT, values)}
        />
      )}

      <p className="text-tertiary text-xs">{interpolateMandate(mandateTemplate, values)}</p>

      <Button
        type="button"
        size="xl"
        color="primary"
        className="w-full"
        onClick={onSubmit}
        isDisabled={blockedOnAck}
        isLoading={isSubmitting}
      >
        {submitLabel}
      </Button>
      <Button type="button" size="md" color="link-gray" className="w-full" onClick={onBack} isDisabled={isSubmitting}>
        Back
      </Button>
    </div>
  );
};

MandateAuthorizationBase.displayName = 'MandateAuthorizationBase';

export default MandateAuthorizationBase;
